import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Line } from 'react-native-svg';

import GradientPlate, { GradientStops } from '../common/GradientPlate';
import { Pressable } from '../common/Touchable';
import { FittedGoldLine, FONT } from '../Profile/BuyVip/vipClubShared';
import RedesignModal from './RedesignModal';
import { useRedesignScale } from './useRedesignScale';

/**
 * The create-VIP-table error, drawn in the redesign popups' chrome like the form it opens over
 * (VipTablesModal): the app backdrop inside a gold rim, a red close disc on the corner, a gold
 * title, the message and one orange button. Numbers are design units, scaled once.
 */
const PANEL = { height: 236, radius: 17, rim: 2 };
const LAYOUT = {
	paddingTop: 30,
	sideText: 28,
	titleHeight: 30,
	buttonsTop: 16,
	buttonWidth: 180,
	buttonHeight: 44,
	bottom: 22,
};
const CLOSE = { size: 37, right: -7, top: 1 };

const colors = {
	text: '#FFF8F4',
	panelRim: '#FFC261',
	panelTint: 'rgba(8,0,0,0.5)',
	scrim: 'rgba(0,0,0,0.68)',
} as const;

const RED_FILL: GradientStops = [
	['0', '#B22306'],
	['0.5', '#6E0900'],
	['1', '#A00C00'],
];
const RED_RIM: GradientStops = [
	['0', '#FFEA96'],
	['0.5', '#FFB844'],
	['1', '#FF7F23'],
];
const ORANGE_FILL: GradientStops = [
	['0', '#FDB20A'],
	['0.4', '#F49D02'],
	['0.85', '#EC8B03'],
	['1', '#EF9700'],
];
const ORANGE_RIM: GradientStops = [
	['0', '#FFE56E'],
	['0.5', '#FFCB42'],
	['1', '#F2A010'],
];

/** Keeps an icon above its absolutely positioned GradientPlate on web (see FriendsFilterModal). */
const IconBox = ({ children }: { children: React.ReactNode }) => (
	<View pointerEvents='none'>{children}</View>
);

interface ErrorModalProps {
	visible: boolean;
	title: string;
	message: string;
	onClose: () => void;
	buttonText?: string;
}

const ErrorVipModal: React.FC<ErrorModalProps> = ({
	visible,
	title,
	message,
	onClose,
	buttonText = 'OK',
}) => {
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const s = (n: number) => n * scale;
	const styles = useMemo(() => createStyles(scale), [scale]);

	if (!visible) return null;

	const closeButton = (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel='Close'
			onPress={onClose}
			hitSlop={6}
			style={styles.close}
		>
			<GradientPlate
				width={s(CLOSE.size)}
				height={s(CLOSE.size)}
				radius={s(CLOSE.size / 2)}
				stops={RED_FILL}
				strokeStops={RED_RIM}
				strokeWidth={Math.max(1, s(2))}
			/>
			<IconBox>
				<Svg width={s(15)} height={s(15)} viewBox='0 0 15 15'>
					<Line
						x1={2}
						y1={2}
						x2={13}
						y2={13}
						stroke='#FFEAA4'
						strokeWidth={2.8}
						strokeLinecap='round'
					/>
					<Line
						x1={13}
						y1={2}
						x2={2}
						y2={13}
						stroke='#FFEAA4'
						strokeWidth={2.8}
						strokeLinecap='round'
					/>
				</Svg>
			</IconBox>
		</Pressable>
	);

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				borderColor: colors.panelRim,
				borderWidth: PANEL.rim,
				tint: colors.panelTint,
				scrimColor: colors.scrim,
				overScreen: true,
			}}
			panelOverlay={closeButton}
		>
			<View style={styles.body}>
				<FittedGoldLine
					text={title.toUpperCase()}
					maxWidth={s(358 - LAYOUT.sideText * 2)}
					height={s(LAYOUT.titleHeight)}
					fontSize={s(22)}
					align='center'
					shadow
				/>

				<View style={styles.messageBox}>
					<Text style={styles.message}>{message}</Text>
				</View>

				<Pressable accessibilityRole='button' onPress={onClose} style={styles.button}>
					<GradientPlate
						width={s(LAYOUT.buttonWidth)}
						height={s(LAYOUT.buttonHeight)}
						radius={s(10)}
						stops={ORANGE_FILL}
						strokeStops={ORANGE_RIM}
						strokeWidth={Math.max(1, s(1.5))}
					/>
					<Text numberOfLines={1} style={styles.buttonLabel}>
						{buttonText.toUpperCase()}
					</Text>
				</Pressable>
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingTop: s(LAYOUT.paddingTop),
			paddingBottom: s(LAYOUT.bottom),
			paddingHorizontal: s(LAYOUT.sideText),
			alignItems: 'center',
		},
		messageBox: {
			flex: 1,
			justifyContent: 'center',
		},
		message: {
			fontFamily: FONT,
			fontSize: s(16),
			lineHeight: s(22),
			fontWeight: '700',
			color: colors.text,
			textAlign: 'center',
		},
		button: {
			width: s(LAYOUT.buttonWidth),
			height: s(LAYOUT.buttonHeight),
			marginTop: s(LAYOUT.buttonsTop),
			borderRadius: s(10),
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#FF9A00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.55,
			shadowRadius: s(8),
		},
		buttonLabel: {
			fontFamily: FONT,
			fontSize: s(16),
			fontWeight: '900',
			color: '#FFFFFF',
			textShadowColor: 'rgba(80,30,0,0.45)',
			textShadowOffset: { width: 0, height: s(1) },
			textShadowRadius: s(1.5),
		},
		close: {
			position: 'absolute',
			right: s(CLOSE.right),
			top: s(CLOSE.top),
			width: s(CLOSE.size),
			height: s(CLOSE.size),
			borderRadius: s(CLOSE.size / 2),
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#FF6A00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.6,
			shadowRadius: s(6),
		},
	});
};

export default ErrorVipModal;
